import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportAgent, AirportStation, AirportZone, AirportPassenger, AirportBag, AirportStaff, AirportPlane, AirportTrash, AirportRoute, AirportPilot } from "../components/index.js";
import { applyReward, prestigeAward, debit, unlockCost, MONEY_LIMIT } from "../domain/economy.js";
import { graduationMissing, tier, AIRLINE_MISSION } from "../domain/airports.js";
import { ECONOMY_KEYS, validateSave, freshTerminalEconomy, freshWallet, freshStation, freshVending } from "../domain/save.js";
import { CONFIG, KIOSK, SPAWN, ADMIN, walkableAirportPoint } from "../domain/config.js";
import { gatePlanKey, gateLayout } from '../domain/layout.js';
import { freshOps, shiftsActive, TRAINING_FRAMES, fastTrackAvailable, refitCost, OPTIONAL_DEPARTMENTS, RETAIL_MODULES } from '../domain/act2.js';
import { trainingCost } from '../services/act2.js';
import { specialistCost, roleLabel, staffName, type Specialist } from '../domain/staff.js';
import { contractAirport, standUpgradeCost, STAND_NAMES, offers } from '../domain/contracts.js';
import { addDiary } from '../services/diary.js';
import { airlineOf, refusal, saveAirline, sign } from '../services/contracts.js';
import { state, career, ids, stations, stationIds, route, stop, distance, feedback, notice, hireStaff, hireSpecialist, zoneAvailable, relayoutAirport, vending, syncGates, restoreAirport } from "../services/world.js";
import { transferIn, transferOut } from "../services/transfers.js";
import { operationsCommand, makePilot } from "../services/operations.js";
import { retainCurrentTerminal } from "../services/retained.js";
import { freshOperations } from "../domain/operations.js";
import { purchaseFacility, purchaseEquipment, beginUpgradePuzzle, cancelUpgrade } from "../services/purchases.js";
import type { Reward } from "../domain/types.js";
@System(AirportState)
export class AirportCommands extends SystemImpl<GameModel> {
    static depth = 100;
    run = (g: GameModel) => {
        const e = state(g);
        for (const id of ids(g, AirportAgent)) {
            const input = g.getTypedUnsafe(PlayerInput, id), agent = g.getTypedUnsafe(AirportAgent, id);
            for (const raw of (input.events ?? []).slice(0, 16)) {
                if (typeof raw !== 'string' || raw.length > 96000 || !raw.startsWith('airport:'))
                    continue;
                const split = raw.indexOf('::');
                if (split < 0)
                    continue;
                const command = raw.slice(0, split);
                let data: any;
                try {
                    data = JSON.parse(raw.slice(split + 2));
                }
                catch {
                    continue;
                }
                if (!data || typeof data !== 'object' || Array.isArray(data))
                    continue;
                if (command === 'airport:load') {
                    if (e.loaded || id !== g.players[0])
                        continue;
                    const save = validateSave(data.save);
                    e.loaded = true;
                    e.loadFrame = g.frame;
                    if (save) {
                        const elapsed = typeof data.elapsedMs === 'number' ? data.elapsedMs : 0;
                        restoreAirport(g,save,elapsed);
                        notice(g,'Welcome back, Captain. Your terminal is ready.');
                    }
                    continue;
                }
                if (!e.loaded)
                    continue;
                if (agent.entering && ['airport:move', 'airport:stop', 'airport:focus'].includes(command)) continue;
                if (command === 'airport:upgradePuzzleBegin') { beginUpgradePuzzle(g, id, data); continue; }
                if (command === 'airport:upgradeCancel') { cancelUpgrade(g, id); continue; }
                if (command === 'airport:operations') { operationsCommand(g, id, data); continue; }
                if (command === 'airport:hire') {
                    // MGT-001: specialists hired at the admin desk. Cash runners exist only after Act I.
                    const role = data.role as Specialist;
                    if (!['porter', 'cleaner', 'runner'].includes(role) || role === 'runner' && !shiftsActive(e)) continue;
                    if (distance(g.getTypedUnsafe(Transform, id), ADMIN) > 60) continue;
                    const count = ids(g, AirportStaff).filter(s => g.getTypedUnsafe(AirportStaff, s).role === role).length;
                    const price = specialistCost(role, e, count);
                    if (data.cost !== price || !debit(career(g), price)) continue;
                    hireSpecialist(g, role); e.revision++;
                    notice(g, `${roleLabel({ role, homeStage: -1 })} hired. They walk in through the front door.`, 2);
                    continue;
                }
                if (command === 'airport:train') {
                    // MGT-003: two minutes off the floor for one more skill pip (max 3); never twice at once. Act II only.
                    const staffId = ids(g, AirportStaff).find(s => g.getTypedUnsafe(AirportStaff, s).recordId === data.staff);
                    if (staffId === undefined || !shiftsActive(e) || distance(g.getTypedUnsafe(Transform, id), ADMIN) > 60) continue;
                    const s = g.getTypedUnsafe(AirportStaff, staffId);
                    if (s.role === 'pilot' || s.training > 0 || s.skill >= 3) continue;
                    const price = trainingCost(s.skill, tier(e).costMult);
                    if (data.cost !== price || !debit(career(g), price)) continue;
                    s.training = TRAINING_FRAMES; s.onBreak = false; e.revision++;
                    notice(g, `${staffName(s.recordId)} is training for ${TRAINING_FRAMES / 3600} minutes.`, 1);
                    continue;
                }
                if (command === 'airport:sign') {
                    // MGT-015: sign this shift's offer onto a free, compatible gate (refusals explain themselves).
                    if (!Number.isInteger(data.offer) || !Number.isInteger(data.gate)) continue;
                    const why = refusal(g, data.offer, data.gate);
                    if (why) { feedback(g, id, why); continue; }
                    const signed = offers(e, e.shift, airlineOf(g))[data.offer];
                    sign(g, data.offer, data.gate);
                    notice(g, `Contract signed for Gate ${String.fromCharCode(65 + data.gate)}.`, 1);
                    if (signed) addDiary(g, `${signed.airline} signed for Gate ${String.fromCharCode(65 + data.gate)} at ${tier(e).name}.`);
                    continue;
                }
                if (command === 'airport:stand') {
                    // MGT-016: upgrade a gate's stand Small -> Medium -> Large at the admin desk.
                    if (!Number.isInteger(data.gate) || !contractAirport(e) || distance(g.getTypedUnsafe(Transform, id), ADMIN) > 60) continue;
                    const a = airlineOf(g), stand = a.stands[data.gate];
                    if (stand === undefined) continue;
                    const price = standUpgradeCost(stand, e);
                    if (!Number.isFinite(price) || data.cost !== price || !debit(career(g), price)) continue;
                    a.stands[data.gate] = stand + 1; saveAirline(g, a);
                    notice(g, `Gate ${String.fromCharCode(65 + data.gate)} now has a ${STAND_NAMES[stand + 1]} stand.`, 1);
                    continue;
                }
                if (command === 'airport:policy') {
                    // MGT-017: any captain may set a policy; it changes the simulation from now on (no free cost reset or rest).
                    const field = ({ service: 'policyService', boarding: 'policyBoarding', breaks: 'policyBreak' } as const)[data.kind as 'service' | 'boarding' | 'breaks'];
                    const max = data.kind === 'service' ? 2 : 1;
                    if (!field || !shiftsActive(e) || !Number.isInteger(data.value) || data.value < 0 || data.value > max) continue;
                    e[field] = data.value; e.revision++;
                    continue;
                }
                if (command === 'airport:fasttrack') {
                    // MGT-008: reserve one Security lane for premium travellers (needs two lanes and 3★); switching off is always allowed.
                    const on = data.on === true;
                    if (on && (!shiftsActive(e) || !fastTrackAvailable(e, stations(g)[2].lanes))) continue;
                    e.fastTrack = on ? 1 : 0; e.revision++;
                    continue;
                }
                if (command === 'airport:refit' || command === 'airport:demolish' || command === 'airport:module') {
                    // MGT-006 at the admin desk, Act II only: refit a department up a tier, demolish an optional one for half
                    // its build price back, or switch the retail module (a rebuild costing half the build price).
                    if (!shiftsActive(e) || distance(g.getTypedUnsafe(Transform, id), ADMIN) > 60) continue;
                    const ss = stations(g);
                    if (command === 'airport:refit') {
                        const s = ss[data.stage], key = `tier${data.stage}` as 'tier0';
                        if (!Number.isInteger(data.stage) || !s?.unlocked) continue;
                        const price = refitCost(CONFIG.stages[data.stage].baseCost, e[key], tier(e).costMult);
                        if (!Number.isFinite(price) || data.cost !== price || !debit(career(g), price)) continue;
                        e[key]++; e.revision++;
                        notice(g, `${CONFIG.stages[data.stage].name} refitted to tier ${e[key] + 1}.`, 1);
                    } else if (command === 'airport:demolish') {
                        const s = ss[data.stage];
                        if (!OPTIONAL_DEPARTMENTS.includes(data.stage) || !s?.unlocked) continue;
                        const refund = Math.floor(unlockCost(data.stage, e) / 2);
                        Object.assign(s, freshStation(data.stage), { activeWorkers: 0, busy: 0, queueLength: 0 });
                        e[`tier${data.stage}` as 'tier0'] = 0;
                        career(g).money = Math.min(MONEY_LIMIT, career(g).money + refund); e.revision++;
                        notice(g, `${CONFIG.stages[data.stage].name} demolished · ${refund.toLocaleString('en-US')} bills refunded. Its slot can be built again.`, 1);
                    } else {
                        const value = data.value, price = Math.ceil(unlockCost(4, e) / 2);
                        if (!Number.isInteger(value) || value < 0 || value > 2 || value === e.retailModule || !ss[4]?.unlocked || data.cost !== price || !debit(career(g), price)) continue;
                        e.retailModule = value; e.revision++;
                        notice(g, `The shop slot is now a ${RETAIL_MODULES[value].name}.`, 1);
                    }
                    continue;
                }
                if (command === 'airport:transfer') {
                    // MGT-002: move a helper between owned airports (Act II on); refusals say why.
                    const why = data.direction === 'in' ? transferIn(g, String(data.airport), String(data.staff)) : transferOut(g, String(data.staff), String(data.airport));
                    if (why) feedback(g, id, why); else notice(g, data.direction === 'in' ? 'A helper is on their way from your other airport.' : 'Helper transferred.', 1);
                    continue;
                }
                if (command === 'airport:assign') {
                    // MGT-002: give a helper a home desk at this airport, or let them roam (-1). Pilots, porters and cleaners keep their jobs.
                    const staffId = ids(g, AirportStaff).find(s => g.getTypedUnsafe(AirportStaff, s).recordId === data.staff);
                    const home = data.home, ok = Number.isInteger(home) && (home === -1 || tier(e).stations.includes(home) && stations(g)[home]?.unlocked);
                    if (staffId === undefined || !ok) continue;
                    const s = g.getTypedUnsafe(AirportStaff, staffId);
                    if (s.role !== 'crew' && s.role !== 'clerk') continue;
                    if (home === -1) Object.assign(s, { role: 'crew', homeStage: -1 });
                    else Object.assign(s, { homeStage: home, ...(s.role === 'clerk' ? { stage: home } : {}) });
                    e.revision++;
                    continue;
                }
                if (command === 'airport:relaxed') {
                    // The room's pace belongs to the host, like the career saves.
                    if (id === g.players[0] && e.relaxed !== !!data.on) { e.relaxed = !!data.on; notice(g, e.relaxed ? 'Relaxed pace: nobody walks out and passengers wait twice as long.' : 'Standard pace: long waits can make passengers walk out.'); }
                    continue;
                }
                if (command === 'airport:equipment') { purchaseEquipment(g, id, data); continue; }
                if (command === 'airport:choose') { purchaseFacility(g, id, data); continue; }
                if (command === 'airport:move') {
                    if (agent.lastCommandFrame === g.frame)
                        continue;
                    const x = data.x, y = data.y;
                    if (typeof x !== 'number' || typeof y !== 'number' || !walkableAirportPoint({ x, y }, tier(e).maxX, gateLayout(tier(e).code).OUTLINE))
                        continue;
                    if (Number.isInteger(data.zone) && data.zone >= 0 && (!g.hasComponent(AirportZone, data.zone) || !zoneAvailable(g, data.zone))) continue;
                    cancelUpgrade(g, id);
                    agent.lastCommandFrame = g.frame;
                    agent.intent = -1;
                    agent.feedbackUntil = 0;
                    agent.building = -1;
                    agent.dwell = 0;
                    let target = { x: Math.round(x), y: Math.round(y) };
                    if (Number.isInteger(data.zone) && data.zone >= 0 && g.hasComponent(AirportZone, data.zone) && zoneAvailable(g, data.zone)) {
                        const p = g.getTypedUnsafe(Transform, data.zone);
                        target = { x: p.x, y: p.y };
                        agent.intent = data.zone;
                    }
                    if (!route(g, id, target, true))
                        feedback(g, id, 'No clear route to that point.');
                    continue;
                }
                if (command === 'airport:focus') {
                    agent.inputLocked = data.locked === true;
                    if (agent.inputLocked)
                        stop(g, id);
                    continue;
                }
                if (command === 'airport:stop') {
                    stop(g, id);
                    agent.intent = -1;
                    agent.building = -1;
                    continue;
                }
                if (command === 'airport:reward') {
                    const kind = data.kind as Reward;
                    if (!['income', 'cash', 'express', 'offline'].includes(kind))
                        continue;
                    // Reward puzzles are local, as in the supplied plan; shared state validates range and cooldown.
                    if (kind !== 'offline' && distance(g.getTypedUnsafe(Transform, id), KIOSK) > 115) {
                        feedback(g, id, 'Walk to the Math Radio first.');
                        continue;
                    }
                    if (applyReward(e, career(g), stations(g), kind, g.frame, agent))
                        notice(g, kind === 'income' ? 'Double income for one minute!' : kind === 'express' ? 'Express service for one minute!' : kind === 'offline' ? 'Your welcome-back bonus is ready to collect.' : 'Math Radio delivered a funding bonus.');
                    else
                        feedback(g, id, 'That reward is cooling down.');
                    continue;
                }
                if (command === 'airport:prestige') {
                    if (data.confirm !== true)
                        continue;
                    const gain = prestigeAward(e, stations(g));
                    if (!gain) {
                        feedback(g, id, `Not ready yet: ${graduationMissing(e, stations(g)).join(' · ')}.`);
                        continue;
                    }
                    const leaving = tier(e);
                    const points = career(g).prestige + gain, run = e.run + 1, epoch = e.prestigeEpoch + 1;
                    // Moving on keeps the airport as a frozen owned record; past the last tier the same airport restarts as before.
                    if (tier({ run }).code !== leaving.code) {
                        const retained = retainCurrentTerminal(g);
                        if (!retained.ok) { feedback(g, id, 'This airport could not be kept safely, so the move was cancelled.'); continue; }
                    }
                    for (const c of [AirportPassenger, AirportBag, AirportStaff, AirportTrash, AirportPlane, AirportPilot])
                        for (const eid of ids(g, c))
                            g.removeEntity(eid);
                    Object.assign(e, freshTerminalEconomy(), freshOps(), { airline: '' }, { run, loaded: true, prestigeEpoch: epoch, noticeUntil: 0, noticePriority: 0, spawnClock: 120, serial: 0 });
                    // The existing graduation still restarts the wallet; reputation accrues on the career.
                    // The Promotion (leaving Summit Ridge) and the Founding (leaving Metro) are pure inheritance: the wallet and any
                    // Board advance carry over. Other moves still restart the wallet, as the Act I journey is tuned for.
                    if (leaving.code === 'SMR' || leaving.code === 'MTR') career(g).prestige = points;
                    else Object.assign(career(g), freshWallet(), { prestige: points, advance: 0 });
                    stationIds(g).forEach((sid, index) => {
                        Object.assign(g.getTypedUnsafe(AirportStation, sid), freshStation(index), { activeWorkers: 0, busy: 0, queueLength: 0 });
                        if (CONFIG.stages[index].starterCrew) hireStaff(g, index);
                    });
                    if (gatePlanKey(leaving.code) !== gatePlanKey(tier(e).code)) relayoutAirport(g);
                    vending(g).forEach((v, i) => Object.assign(v, freshVending()[i]));
                    ids(g, AirportRoute).forEach(rid => { const r = g.getTypedUnsafe(AirportRoute, rid); Object.assign(r, freshOperations().routes[r.index]); });
                    syncGates(g);
                    for (const a of ids(g, AirportAgent)) {
                        Object.assign(g.getTypedUnsafe(Transform, a), SPAWN);
                        Object.assign(g.getTypedUnsafe(AirportAgent, a), { entering: false, intent: -1, pad: -1, dwell: 0, building: -1, completedPad: -1, upgradeTicket: "", inputLocked: false });
                        stop(g, a);
                    }
                    const arriving = tier(e);
                    if (arriving !== leaving) addDiary(g, `Moved from ${leaving.name} to ${arriving.name} (+${gain} reputation).`);
                    if (arriving.code === 'BVM' && leaving.code === 'SMR') takeOverBayview(g);
                    if (leaving.code === 'MTR') foundAirline(g);
                    notice(g, arriving === leaving ? `New terminal at ${arriving.name}: +${gain} reputation.` : `Welcome to ${arriving.name}! ${leaving.name} now sends you income. +${gain} reputation.`);
                }
            }
        }
    };
}
/**
 * MGT-022 Bayview takeover: the Promotion lands in a running but rough airport, with core desks open, three tired
 * one-pip helpers on Check-In, Bag Drop and Boarding (Security uncovered), two Small and one Medium stand and a 1-star
 * rating. The first beats teach assigning a helper and fixing a crisis by hand.
 */
function takeOverBayview(g: GameModel) {
    const e = state(g), ss = stations(g);
    for (const i of [0, 1, 2, 6]) Object.assign(ss[i], { unlocked: true, level: Math.max(1, ss[i].level) });
    for (const id of ids(g, AirportStaff)) if (g.getTypedUnsafe(AirportStaff, id).role !== 'pilot') g.removeEntity(id);
    for (const i of [0, 1, 6]) {
        ss[i].manager = true;
        const id = hireStaff(g, i), s = g.getTypedUnsafe(AirportStaff, id);
        Object.assign(s, { role: 'crew', energy: 40, skill: 1 });
    }
    e.starsOverall = 10;
    notice(g, 'Welcome to Bayview! The team is tired and Security has nobody. Open Helpers at the admin desk and assign someone to Security.', 3);
}
/**
 * MGT-025 the Founding: mastering Metro founds Sky Harbor Airways on arrival at Pacific Gateway. Pure inheritance: the
 * wallet already carries over, no earnings threshold, and a heritage pilot joins for one bill.
 */
function foundAirline(g: GameModel) {
    const e = state(g);
    e.mission = AIRLINE_MISSION + 1;
    career(g).money = Math.max(0, career(g).money - 1);
    makePilot(g, 0, 0); e.pilotCount = 1; e.revision++;
    notice(g, 'The Founding! Sky Harbor Airways is born, and a heritage pilot joins for one bill. Your own aircraft take over at the next turnaround.', 3);
    addDiary(g, 'The Founding: Sky Harbor Airways was born at Pacific Gateway.');
}

